import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Users, CheckCircle, Gift } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDirection } from '@/providers/DirectionProvider';
import i18n from '@/i18n/config';

// Lazy load RequestForm to reduce initial bundle size
const RequestForm = lazy(() => import('@/components/RequestForm'));

interface HeroSectionProps {
  scrollToRequestForm: () => void;
  scrollToSearchSection: () => void;
  initialProfession?: string | null;
  professionLabel?: string | null;
  isLoadingProfession?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  scrollToRequestForm,
  scrollToSearchSection,
  initialProfession,
  professionLabel,
  isLoadingProfession = false
}) => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const isMobile = useIsMobile();
  const isHebrew = i18n.language === 'he';
  const steps = [{
    icon: FileText,
    label: t('hero.step1'),
    color: 'bg-brand-navy'
  }, {
    icon: Users,
    label: t('hero.step2'),
    color: 'bg-brand-teal'
  }, {
    icon: CheckCircle,
    label: t('hero.step3'),
    color: 'bg-brand-amber'
  }];
  return <section className="relative min-h-screen pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden w-full">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-primary to-brand-teal z-0" />
      
      {/* Mesh gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-amber/15 via-transparent to-transparent z-0" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand-teal/20 via-transparent to-transparent z-0" />
      
      {/* Floating decorative orbs - using fixed dimensions and contain-intrinsic-size to prevent CLS */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-brand-teal/20 rounded-full blur-3xl hidden lg:block pointer-events-none" style={{ contain: 'strict', contentVisibility: 'auto', containIntrinsicSize: '288px 288px' }} aria-hidden="true" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-brand-amber/15 rounded-full blur-3xl hidden lg:block pointer-events-none" style={{ contain: 'strict', contentVisibility: 'auto', containIntrinsicSize: '384px 384px' }} aria-hidden="true" />
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-primary/10 rounded-full blur-3xl hidden lg:block pointer-events-none" style={{ contain: 'strict', contentVisibility: 'auto', containIntrinsicSize: '256px 256px' }} aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Hero Header */}
        <div className="text-center mb-8 md:mb-12 animate-fade-in-up my-[52px]">
          <div className="inline-flex flex-col items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-4 py-3 mb-6">
            <span className="text-white/90 font-semibold text-sm">{t('hero.badge')}</span>
            <div className="flex items-center gap-2 text-brand-amber text-sm font-semibold">
              <Gift className="w-4 h-4" />
              <span className="font-bold text-lg">{t('hero.cashbackBadge')}</span>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            {t('hero.findPerfect')}
            <span className="text-brand-teal-light mx-2 relative">
              {isLoadingProfession ? (
                <span className="inline-block w-20 h-8 md:h-10 lg:h-12 bg-white/20 rounded animate-pulse align-middle" />
              ) : (
                professionLabel ? `ה${professionLabel}` : t('hero.professional')
              )}
              {!isLoadingProfession && (
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                  <path d="M2 6C50 2 150 2 198 6" stroke="hsl(var(--brand-amber))" strokeWidth="3" strokeLinecap="round" />
                </svg>
              )}
            </span>
            <br className="hidden md:block" />
            {t('hero.forEveryJob')}
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
            {isMobile ? t('hero.descriptionMobile') : t('hero.description')}
          </p>
          
          {/* Multilingual message - shown only for non-Hebrew languages */}
          {!isHebrew && (
            <h2 className="text-lg md:text-xl text-white/90 mt-4 max-w-2xl mx-auto font-medium bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
              {t('hero.multilingualMessage')}
            </h2>
          )}
        </div>

        {/* Mini Steps - How it works */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-8 md:mb-12 animate-fade-in">
          {steps.map((step, index) => <React.Fragment key={index}>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <step.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <span className="text-white font-medium text-sm md:text-base">{step.label}</span>
              </div>
              
              {/* Connector line - hidden on mobile and after last item */}
              {index < steps.length - 1 && <div className="hidden md:block w-12 h-0.5 bg-gradient-to-r from-brand-teal to-brand-amber rounded-full" />}
            </React.Fragment>)}
        </div>

        {/* Form Container with Glassmorphism */}
        <div className="max-w-lg mx-auto animate-fade-in-up" style={{
        animationDelay: '0.2s'
      }}>
          {/* Gradient border glow effect */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-brand-teal via-brand-amber to-brand-teal rounded-3xl blur opacity-40 group-hover:opacity-60 transition duration-500" />
            
            <div id="request-form" className="relative backdrop-blur-sm bg-white/95 rounded-3xl shadow-2xl overflow-hidden border border-white/50">
              {/* Decorative top gradient bar */}
              <div className="h-1.5 bg-gradient-to-r from-brand-teal via-brand-amber to-brand-teal" />
              
              <Suspense fallback={<div className="p-6 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-12 bg-gray-200 rounded"></div>
                  </div>
                </div>}>
                <RequestForm initialProfession={initialProfession} />
              </Suspense>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-6 mt-6 text-white/70 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-brand-teal" />
              <span>{t('hero.trustIndicator1')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-brand-teal" />
              <span>{t('hero.trustIndicator2')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 z-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
        </svg>
      </div>
    </section>;
};
export default HeroSection;